import type { SupportCompanyRow } from '@siteday/shared';
import { useQuery } from '@tanstack/react-query';
import { Plus, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import * as supportApi from '@/api/support';
import { qk } from '@/api/support';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/states';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, EmptyRow, Table, Th } from '@/components/ui/table';
import { fmtDate } from '@/lib/format';
import { sortRows, useTableSort } from '@/lib/sort';

import { adoptionPill, adoptionRank } from './adoption';

const getters: Record<string, (r: SupportCompanyRow) => string | number | null> = {
  name: (r) => r.name,
  adoption: (r) => adoptionRank(r.adoption),
  entries7: (r) => r.entriesLast7,
  entries30: (r) => r.entriesLast30,
  lastEntry: (r) => r.lastEntryDate,
  foremen: (r) => r.counts.foremen,
  jobs: (r) => r.counts.activeJobs,
  qbo: (r) => (r.qboConnected ? 1 : 0),
  support: (r) => (r.supportAccessAllowed ? 1 : 0),
  created: (r) => r.createdAt,
};

/** Every company at a glance: adoption first, then the numbers a support call starts from. */
export function CompaniesPage() {
  const companies = useQuery({ queryKey: qk.companies(), queryFn: supportApi.listCompanies, refetchInterval: 60_000 });
  const sort = useTableSort('adoption', 'asc');
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const rows = useMemo(() => {
    const items = companies.data?.items ?? [];
    const filtered = q.trim() ? items.filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase())) : items;
    return sortRows(filtered, sort.column, sort.dir, getters);
  }, [companies.data, q, sort.column, sort.dir]);

  const attention = (companies.data?.items ?? []).filter((r) => r.adoption === 'alerted').length;

  return (
    <>
      <PageHeader
        title="Companies"
        sub={
          companies.data
            ? `${companies.data.items.length} compan${companies.data.items.length === 1 ? 'y' : 'ies'}${attention ? ` · ${attention} went quiet` : ''}`
            : null
        }
        actions={
          <>
            <Button onClick={() => void companies.refetch()} loading={companies.isFetching && !companies.isPending} aria-label="Refresh">
              <RefreshCw /> Refresh
            </Button>
            <Link to="/companies/new">
              <Button variant="primary">
                <Plus /> New company
              </Button>
            </Link>
          </>
        }
      />
      <div className="mb-3.5 flex items-center gap-2">
        <Input aria-label="Filter companies" placeholder="Filter by name…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" />
      </div>
      {companies.isPending ? (
        <LoadingState />
      ) : companies.isError ? (
        <ErrorState error={companies.error} onRetry={() => void companies.refetch()} />
      ) : companies.data.items.length === 0 ? (
        <Card>
          <EmptyState>No companies yet. Create the first one.</EmptyState>
        </Card>
      ) : (
        <Card>
          <Table data-testid="companies-table">
            <thead>
              <tr>
                <Th sortKey="name" sort={sort}>
                  Company
                </Th>
                <Th sortKey="adoption" sort={sort}>
                  Adoption
                </Th>
                <Th right sortKey="entries7" sort={sort}>
                  Entries 7d
                </Th>
                <Th right sortKey="entries30" sort={sort}>
                  30d
                </Th>
                <Th sortKey="lastEntry" sort={sort}>
                  Last entry
                </Th>
                <Th right sortKey="foremen" sort={sort}>
                  Foremen
                </Th>
                <Th right sortKey="jobs" sort={sort}>
                  Active jobs
                </Th>
                <Th sortKey="qbo" sort={sort}>
                  QBO
                </Th>
                <Th sortKey="support" sort={sort}>
                  Support access
                </Th>
                <Th sortKey="created" sort={sort}>
                  Created
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <EmptyRow colSpan={10}>No company matches “{q}”.</EmptyRow>
              ) : (
                rows.map((r) => {
                  const pill = adoptionPill(r.adoption);
                  return (
                    <tr key={r.id} className="cursor-pointer" onClick={() => navigate(`/companies/${r.id}`)} data-testid={`company-row-${r.id}`}>
                      <td>
                        <Link to={`/companies/${r.id}`} className="font-bold hover:underline" onClick={(e) => e.stopPropagation()}>
                          {r.name}
                        </Link>
                        <span className="sub">
                          {r.timezone} · {r.plan}
                        </span>
                      </td>
                      <td>
                        <Pill tone={pill.tone} title={pill.hint}>
                          {pill.label}
                        </Pill>
                      </td>
                      <td className="r num">{r.entriesLast7}</td>
                      <td className="r num">{r.entriesLast30}</td>
                      <td className="num">{fmtDate(r.lastEntryDate)}</td>
                      <td className="r num">{r.counts.foremen}</td>
                      <td className="r num">{r.counts.activeJobs}</td>
                      <td>{r.qboConnected ? <Pill tone="good">Connected</Pill> : <Pill>Not connected</Pill>}</td>
                      <td>{r.supportAccessAllowed ? <Pill tone="info">Allowed</Pill> : <Pill tone="bad">Revoked</Pill>}</td>
                      <td className="num">{fmtDate(r.createdAt.slice(0, 10))}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}
